import CloseIcon from '@mui/icons-material/Close';
import Diversity2Icon from '@mui/icons-material/Diversity2';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Select, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addNotary, changeChargeTransactionStatus, downloadTransactionsCSV } from 'src/api/pmApi';
import { SiteHeader } from 'src/components/ui/SiteHeader';
import { UploadButton } from 'src/components/ui/UploadButton';
import { Routes as RoutesData } from 'src/router/Routes';
import { TransactionsTable } from './TransactionsTable';
import { LinkItem } from '../../components/ui/LinkItem';
import { CustomDatePicker } from 'src/components/mui-overrides/Datepicker';
import { useSerialNumbers } from 'src/hooks/useSerialNumbers';

interface TransactionsProps {
  status: 'reserved' | 'success';
}

export const Transactions: FC<TransactionsProps> = ({ status }) => {
  const [paymentModal, setPaymentModal] = useState<boolean>(false);
  const [notaryModal, setNotaryModal] = useState<boolean>(false);
  const [downloadCSVModal, setDownloadCSVModal] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(false);
  const { t } = useTranslation();
  const [chargeTransactionId, setChargeTransactionId] = useState<string>('');
  const [transactionId, setTransactionId] = useState<string>('');

  const [assetId, setAssetId] = useState<string>('');
  const [serialNumberModal, setSerialNumberModal] = useState<boolean>(false);

  useEffect(() => {
    setReload(!reload);
  }, [status]);

  const openNotaryModal = (transactionId: string) => {
    setNotaryModal(true);
    setTransactionId(transactionId);
  };

  const openPaymentModal = (transactionId: string) => {
    setPaymentModal(!paymentModal);
    setChargeTransactionId(transactionId);
  };

  const openSerialNumberModal = (props: { transactionId: string; assetId: string }) => {
    console.log('openSerialNumberModal', props);
    setSerialNumberModal(true);
    setTransactionId(props.transactionId);
    setAssetId(props.assetId);
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <SiteHeader
            breadcrumbs={[
              {
                link: RoutesData.TRANSACTIONS.ROOT,
                label: t('transactions.overview.title'),
                icon: <Diversity2Icon sx={{ mr: 0.5 }} fontSize="inherit" />,
              },
            ]}
            headline={t('transactions.overview.title')}
          ></SiteHeader>
        </div>
        <div style={{ display: 'flex', columnGap: '16px' }}>
          <LinkItem
            href={RoutesData.TRANSACTIONS.PENDING}
            label={t('transactions.overview.title.pending')}
            active={status === 'reserved'}
          />
          <LinkItem
            href={RoutesData.TRANSACTIONS.COMPLETED}
            label={t('transactions.overview.title.completed')}
            active={status === 'success'}
          />
        </div>
      </div>
      <PaymentModal
        transactionId={chargeTransactionId}
        open={paymentModal}
        handleClose={() => {
          setPaymentModal(false);
        }}
        setReload={setReload}
      />
      <NotaryModal
        transactionId={transactionId}
        open={notaryModal}
        handleClose={() => {
          setNotaryModal(false);
        }}
        setReload={setReload}
      />
      <DownloadCSVModal
        open={downloadCSVModal}
        handleClose={() => {
          setDownloadCSVModal(false);
        }}
      />
      <SerialNumberModal
        transactionId={transactionId}
        assetId={assetId}
        open={serialNumberModal}
        handleClose={() => {
          setSerialNumberModal(false);
        }}
        setReload={setReload}
      />
      <div style={{ textAlign: 'right' }}>
        <Button
          style={{ border: '1px solid #000', borderRadius: '4px', padding: '8px 16px', margin: '8px', color: '#000' }}
          onClick={() => {
            setDownloadCSVModal(true);
          }}
        >
          Download As CSV
        </Button>
      </div>
      <TransactionsTable
        status={status}
        reload={reload}
        openNotaryModal={openNotaryModal}
        openPaymentModal={openPaymentModal}
        openSerialNumberModal={openSerialNumberModal}
      />
    </>
  );
};

interface Props {
  open: boolean;
  handleClose: () => void;
  transactionId: string;
  setReload: (reload: boolean) => void;
}

const PaymentModal: FC<Props> = ({ open, handleClose, transactionId, setReload }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const onClose = () => {
    handleClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth={true}>
        <DialogTitle>
          <Grid container>
            <Typography padding={1} flexGrow={1} variant="h5" sx={{ fontWeight: 500 }}>
              {t('transactions.modal.payment.title')}
            </Typography>
            <IconButton
              onClick={() => {
                onClose();
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent>{t('transactions.modal.payment.text')}</DialogContent>
        <DialogActions>
          <LoadingButton
            fullWidth={false}
            onClick={async () => {
              try {
                setLoading(true);
                // await changeTransactionStatus(transactionId, 'canceledOffer');
                setLoading(false);
                onClose();
                setReload(true);
              } catch (error) {
                setLoading(false);
              }
            }}
            loading={loading}
            variant="contained"
            disableElevation
            color="secondary"
            style={{ margin: '4px', width: '100px' }}
          >
            {t('transactions.modal.payment.button.cancel')}
          </LoadingButton>
          <LoadingButton
            fullWidth={false}
            onClick={async () => {
              try {
                setLoading(true);
                await new Promise((r) => setTimeout(r, 1000));
                await changeChargeTransactionStatus(transactionId);
                setLoading(false);
                onClose();
                setReload(true);
              } catch (error) {
                setLoading(false);
              }
            }}
            loading={loading}
            variant="contained"
            disableElevation
            color="primary"
            style={{ margin: '4px', width: '100px' }}
          >
            {t('transactions.modal.payment.button.confirm')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const SerialNumberModal: FC<{
  open: boolean;
  handleClose: () => void;
  transactionId: string;
  assetId: string;
  setReload: (reload: boolean) => void;
}> = ({ open, handleClose, transactionId, assetId, setReload }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const serialNumbers = useSerialNumbers({ assetId, transactionId });
  const onClose = () => {
    handleClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth={true}>
        <DialogTitle>
          <Grid container>
            <Typography padding={1} flexGrow={1} variant="h5" sx={{ fontWeight: 500 }}>
              Serial Numbers
            </Typography>
            <IconButton
              onClick={() => {
                onClose();
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent>
          {serialNumbers.map((serialNumber) => {
            return <div>{serialNumber.serialNumber}</div>;
          })}
        </DialogContent>
        <DialogActions>
          <LoadingButton
            fullWidth={false}
            onClick={async () => {
              try {
                setLoading(true);
                // await changeTransactionStatus(transactionId, 'canceledOffer');
                setLoading(false);
                onClose();
                setReload(true);
              } catch (error) {
                setLoading(false);
              }
            }}
            loading={loading}
            variant="contained"
            disableElevation
            color="secondary"
            style={{ margin: '4px', width: '100px' }}
          >
            {t('transactions.modal.payment.button.cancel')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

async function downloadAsCSV({ from, to, status }: { from?: Date; to?: Date; status?: 'reserved' | 'success' }) {
  const data = await downloadTransactionsCSV({ from, to, status });
  const url = window.URL.createObjectURL(new Blob([data as any]));
  const link = document.createElement('a');
  const fileName = `transactions_${new Date().toDateString()}.csv`;
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

const NotaryModal: FC<Props> = ({ open, handleClose, transactionId, setReload }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [fileUrl, setFileUrl] = useState<string>('');
  const { t } = useTranslation();
  const onClose = () => {
    handleClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth={true}>
        <DialogTitle>
          <Grid container>
            <Typography padding={1} flexGrow={1} variant="h5" sx={{ fontWeight: 500 }}>
              {t('transactions.modal.notary.title')}
            </Typography>
            <IconButton
              onClick={() => {
                onClose();
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent>
          {t('transactions.modal.notary.text')}
          <br />
          <UploadButton
            fileUrl={fileUrl}
            setFileUrl={setFileUrl}
            disabled={false}
            key={'upload'}
            tooltipContent="upload notar"
            text={t('transactions.modal.upload.title')}
          ></UploadButton>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            fullWidth={false}
            onClick={onClose}
            loading={loading}
            variant="contained"
            disableElevation
            color="secondary"
            style={{ margin: '4px', width: '100px' }}
          >
            {t('transactions.modal.notary.button.cancel')}
          </LoadingButton>
          <LoadingButton
            fullWidth={false}
            onClick={async () => {
              try {
                setLoading(true);
                await addNotary(transactionId, fileUrl);
                setFileUrl(''); // reset fileUrl
                setLoading(false);
                onClose();
                setReload(true);
              } catch (error) {
                setLoading(false);
                setFileUrl(''); // reset fileUrl
              }
            }}
            loading={loading}
            variant="contained"
            disableElevation
            color="primary"
            style={{ margin: '4px', width: '100px' }}
          >
            {t('transactions.modal.notary.button.confirm')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const lastYear = new Date();
lastYear.setFullYear(lastYear.getFullYear() - 1);

const DownloadCSVModal: FC<{ open: any; handleClose: any }> = ({ open, handleClose }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [from, setFrom] = useState<Date>(lastYear);
  const [to, setTo] = useState<Date>(new Date());
  const [status, setStatus] = useState<'reserved' | 'success'>('success');
  const { t } = useTranslation();
  const onClose = () => {
    handleClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth={true}>
        <DialogTitle>
          <Grid container>
            <Typography padding={1} flexGrow={1} variant="h5" sx={{ fontWeight: 500 }}>
              {t('transactions.modal.notary.title')}
            </Typography>
            <IconButton
              onClick={() => {
                onClose();
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent>
          {t('transactions.modal.notary.text')}
          <br />
          <LoadingButton></LoadingButton>
          <CustomDatePicker label="From" value={from} setValue={setFrom} />
          <CustomDatePicker label="To" value={to} setValue={setTo} />

          <Select
            value={status}
            // placeholder="Status"
            // label="Status"
            onChange={(e) => {
              console.log(e.target.value);
              setStatus(e.target.value as 'reserved' | 'success');
            }}
            style={{ width: '100%' }}
          >
            <MenuItem value="reserved">Reserved</MenuItem>
            <MenuItem value="success">Success</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            fullWidth={false}
            onClick={onClose}
            loading={loading}
            variant="contained"
            disableElevation
            color="secondary"
            style={{ margin: '4px', width: '100px' }}
          >
            {t('transactions.modal.notary.button.cancel')}
          </LoadingButton>
          <LoadingButton
            fullWidth={false}
            onClick={async () => {
              try {
                setLoading(true);
                await downloadAsCSV({ from, to, status });
                setLoading(false);
                onClose();
              } catch (error) {
                setLoading(false);
              }
            }}
            loading={loading}
            variant="contained"
            disableElevation
            color="primary"
            style={{ margin: '4px', width: '100px' }}
          >
            {t('transactions.modal.download')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
